// api/template.service.ts

import { api } from "api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { T_Api_Paginated, T_Api_Success_Res } from "./api.types";

export type Recipients = {
  to: string[];
  cc: string[];
};

export type TemplateDto = {
  _id: string;
  name: string;
  slug: string;
  tags: string[];
  subject: string;
  body: string;
  variables: string[];
  version: number;
  triggers: string[];
  recipients: Recipients;
  organisation: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
};

export type CreateTemplateDTO = {
  name: string;
  subject: string;
  body: string;
  organisation: string;
  tags?: string[];
  triggers?: string[];
  recipients?: Recipients;
};

export type UpdateTemplateDTO = Partial<
  Pick<TemplateDto, "name" | "subject" | "body" | "tags" | "triggers" | "recipients">
>;

export type VariableDetails = {
  key: string;
  datasource: string;
  depth: number;
  description: string;
};

export type VariableMap = Record<string, VariableDetails>;

export const templateService = {
  useGetAllTemplates: (organisationId: string | undefined) => {
    return useQuery({
      queryKey: ["templates", organisationId],
      queryFn: async () => {
        const { data } = await api.get<T_Api_Success_Res<T_Api_Paginated<TemplateDto>>>(`/templates`, {
          params: { organisation: organisationId },
        });
        return data.result;
      },
      enabled: !!organisationId,
    });
  },

  useGetVariables: () => {
    return useQuery({
      queryKey: ["template-variables"],
      queryFn: async () => {
        const res = await api.get(`/templates/variables`);
        return res.data?.result as VariableMap;
      },
    });
  },

  useCreateTemplate: () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: ["template/create"],
      mutationFn: async (body: CreateTemplateDTO) => {
        const res = await api.post(`/templates`, body);
        return res.data?.result as TemplateDto;
      },
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ["templates", data.organisation],
        });
      },
    });
  },

  useUpdateTemplate: () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: ["template/update"],
      mutationFn: async ({ id, body }: { id: string; body: UpdateTemplateDTO }) => {
        const res = await api.put(`/templates/${id}`, body);
        return res.data?.result as TemplateDto;
      },
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ["templates", data.organisation],
        });
      },
    });
  },
};
